/**
 * Product Wishlist Management
 * Handles wishlist functionality with local storage for non-logged-in users
 * and API integration for logged-in users
 */

class ProductWishlist {
    constructor() {
        this.localStorageKey = 'hb_wishlist_items';
        this.wishlistItems = new Set();
        this.isUserLoggedIn = false;

        this.init();
    }
    /**
     * Initialize the wishlist system
     */
    async init() {
        // Load from localStorage cache
        this.loadFromStorage();
        this.bindEvents();

        // Immediately update hearts with cached data (instant feedback)
        this.updateAllHearts();
        this.updateHeaderCount();

        // Check auth status and sync in background (don't await)
        // After sync completes, hearts will be updated again with server data
        this.checkAuthStatus().catch(error => {
            console.error('Background sync failed:', error);
        });
    }

    /**
     * Check if user is authenticated and sync wishlist
     */
    async checkAuthStatus() {
        // Check Shopify customer login status from global config
        const config = window.HB_WISHLIST_CONFIG || {};
        this.isUserLoggedIn = config.isLoggedIn || false;

        // If user is logged in, sync with server (server = source of truth)
        if (this.isUserLoggedIn) {
            console.log('🔐 User is logged in, syncing with server...');

            try {
                // Load wishlist from server (returns array of items with productId, variantId, name, handle)
                const serverItems = await this.loadFromServer();

                // Determine sync strategy based on local vs server state
                const hasLocalItems = this.wishlistItems.size > 0;
                const hasServerItems = serverItems && serverItems.length > 0;

                if (hasLocalItems && !hasServerItems) {
                    // localStorage has items but server is empty
                    // → Upload local items to server, keep localStorage as source of truth
                    console.log('📤 localStorage has items but server is empty - uploading to server and keeping local data');
                    await this.uploadLocalItemsToServer();
                    // Don't call syncWithServer - keep local data as-is
                    this.updateAllHearts();
                    this.updateHeaderCount();
                    this.triggerWishlistEvent('synced', {
                        itemCount: this.wishlistItems.size,
                        isInitialSync: true,
                        strategy: 'local-priority'
                    });
                } else if (hasLocalItems && hasServerItems) {
                    // Both have items - do a full sync (upload local, then use server as source)
                    console.log('🔄 Initial sync detected - will upload local items and merge with server');
                    await this.syncWithServer(serverItems, true);
                } else {
                    // Server has items or both empty - use server as source of truth
                    console.log('🔄 Regular sync - loading from server');
                    await this.syncWithServer(serverItems, false);
                }
            } catch (error) {
                console.error('Error syncing wishlist:', error);
            }
        }
    }

    /**
     * Load wishlist items from localStorage
     */
    loadFromStorage() {
        try {
            const stored = localStorage.getItem(this.localStorageKey);
            if (stored) {
                const items = JSON.parse(stored);
                this.wishlistItems = new Set(items);
            }
        } catch (error) {
            this.wishlistItems = new Set();
        }
    }

    /**
     * Save wishlist items to localStorage
     */
    saveToStorage() {
        try {
            const items = Array.from(this.wishlistItems);
            localStorage.setItem(this.localStorageKey, JSON.stringify(items));
        } catch (error) {
            // Silent fail for localStorage errors
        }
    }

    /**
     * Save individual wishlist item data
     */
    saveWishlistItem(itemData) {
        try {
            // Remove any addedAt timestamp to prevent time-based text injection
            if (itemData.addedAt) {
                delete itemData.addedAt;
            }

            const itemKey = `${this.localStorageKey}_item_${itemData.key}`;
            localStorage.setItem(itemKey, JSON.stringify(itemData));
        } catch (error) {
            // Silent fail for localStorage errors
        }
    }

    /**
     * Get individual wishlist item data
     *
     */
    getWishlistItem(key) {
        try {
            const itemKey = `${this.localStorageKey}_item_${key}`;
            const stored = localStorage.getItem(itemKey);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Remove individual wishlist item data
     */
    removeWishlistItemData(key) {
        try {
            const itemKey = `${this.localStorageKey}_item_${key}`;
            localStorage.removeItem(itemKey);
        } catch (error) {
            // Silent fail for localStorage errors
        }
    }

    /**
     * Check if an item is in the wishlist
     */
    isInWishlist(productId, variantId) {
        const key = `${productId}-${variantId}`;
        return this.wishlistItems.has(key);
    }

    /**
     * Add item to wishlist with complete product data
     */
    async addToWishlist(productId, variantId, productData = null, variantData = null) {
        // Input validation
        if (!productId || !variantId || isNaN(productId) || isNaN(variantId)) {
            return;
        }

        const key = `${productId}-${variantId}`;

        // Always store in localStorage as backup
        const wishlistItem = {
            key: key,
            productId: productId,
            variantId: variantId
        };

        // If product data is provided, store it
        if (productData && variantData) {
            wishlistItem.product = {
                id: productData.id || productId,
                title: productData.title,
                handle: productData.handle,
                vendor: productData.vendor,
                featured_image: productData.featured_image
            };

            wishlistItem.variant = {
                id: variantData.id || variantId,
                title: variantData.title,
                price: variantData.price,
                compare_at_price: variantData.compare_at_price,
                featured_image: variantData.featured_image,
                sku: variantData.sku || ''
            };
        }

        // Add to wishlist items set
        this.wishlistItems.add(key);

        // Save individual item data
        this.saveWishlistItem(wishlistItem);
        this.saveToStorage();

        // If user is logged in, also sync to server
        if (this.isUserLoggedIn) {
            try {
                await this.addToServerWishlist(productId, variantId);
            } catch (error) {
                console.warn('Failed to sync to server, but item is saved locally:', error);
            }
        }

        this.updateHeartForItem(key);
        this.updateHeaderCount();
        this.triggerWishlistEvent('added', {productId, variantId});
    }

    /**
     * Remove item from wishlist
     */
    async removeFromWishlist(productId, variantId) {
        // Input validation
        if (!productId || !variantId || isNaN(productId) || isNaN(variantId)) {
            return;
        }

        const key = `${productId}-${variantId}`;

        // Always remove from localStorage
        this.wishlistItems.delete(key);
        this.removeWishlistItemData(key);
        this.saveToStorage();

        // If user is logged in, also sync to server
        if (this.isUserLoggedIn) {
            try {
                await this.removeFromServerWishlist(productId, variantId);
            } catch (error) {
                console.warn('Failed to sync removal to server, but item is removed locally:', error);
            }
        }

        this.updateHeartForItem(key);
        this.updateHeaderCount();
        this.triggerWishlistEvent('removed', {productId, variantId});
    }

    /**
     * Toggle item in wishlist
     */
    async toggleWishlist(productId, variantId) {
        if (this.isInWishlist(productId, variantId)) {
            await this.removeFromWishlist(productId, variantId);
        } else {
            await this.addToWishlist(productId, variantId);
        }
    }

    /**
     * Toggle item in wishlist with complete product data
     */
    async toggleWishlistWithData(productId, variantId, productData, variantData) {
        if (this.isInWishlist(productId, variantId)) {
            await this.removeFromWishlist(productId, variantId);
        } else {
            await this.addToWishlist(productId, variantId, productData, variantData);
        }
    }

    /**
     * Add to server-side wishlist (for logged-in users)
     */
    async addToServerWishlist(productId, variantId) {
        const config = window.HB_WISHLIST_CONFIG || {};

        const missingConfig = [];
        if (!config.apiBaseUrl) missingConfig.push('apiBaseUrl');
        if (!config.apiToken) missingConfig.push('apiToken');
        if (!config.customerEmail) missingConfig.push('customerEmail');

        if (missingConfig.length > 0) {
            console.error('Wishlist API configuration missing:', missingConfig.join(', '));
            return;
        }

        try {
            const url = `${config.apiBaseUrl}/wishlists/default/items?filter[customer_email]=${encodeURIComponent(config.customerEmail)}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${config.apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    shop_external_id: String(variantId)
                })
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            console.log('Item added to server wishlist:', variantId);
        } catch (error) {
            console.error('Error adding to server wishlist:', error);
            throw error;
        }
    }

    /**
     * Remove from server-side wishlist (for logged-in users)
     */
    async removeFromServerWishlist(productId, variantId) {
        const config = window.HB_WISHLIST_CONFIG || {};

        const missingConfig = [];
        if (!config.apiBaseUrl) missingConfig.push('apiBaseUrl');
        if (!config.apiToken) missingConfig.push('apiToken');
        if (!config.customerEmail) missingConfig.push('customerEmail');

        if (missingConfig.length > 0) {
            console.error('Wishlist API configuration missing:', missingConfig.join(', '));
            return;
        }

        try {
            const url = `${config.apiBaseUrl}/wishlists/default/items?filter[customer_email]=${encodeURIComponent(config.customerEmail)}`;

            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${config.apiToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    shop_external_id: String(variantId)
                })
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            console.log('Item removed from server wishlist:', variantId);
        } catch (error) {
            console.error('Error removing from server wishlist:', error);
            throw error;
        }
    }

    /**
     * Load wishlist from server (for logged-in users)
     * Returns array of wishlist items with product/variant IDs and basic info from API
     */
    async loadFromServer() {
        const config = window.HB_WISHLIST_CONFIG || {};

        const missingConfig = [];
        if (!config.apiBaseUrl) missingConfig.push('apiBaseUrl');
        if (!config.apiToken) missingConfig.push('apiToken');
        if (!config.customerEmail) missingConfig.push('customerEmail');

        if (missingConfig.length > 0) {
            console.error('Wishlist API configuration missing:', missingConfig.join(', '));
            return [];
        }

        try {
            const url = `${config.apiBaseUrl}/wishlists/default/items?filter[customer_email]=${encodeURIComponent(config.customerEmail)}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${config.apiToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();

            // Extract wishlist items with all available data
            // API Response structure:
            // - shop_external_id = Shopify Variant ID
            // - shop_external_product_id = Shopify Product ID
            // - name = Product name
            // - product_url = Full product URL
            const wishlistItems = [];

            if (data.data && Array.isArray(data.data)) {
                data.data.forEach(item => {
                    if (item.shop_external_id && item.shop_external_product_id) {
                        // Extract handle from product_url
                        let handle = '';
                        if (item.product_url) {
                            const urlMatch = item.product_url.match(/\/products\/([^/?]+)/);
                            handle = urlMatch ? urlMatch[1] : '';
                        }

                        wishlistItems.push({
                            productId: String(item.shop_external_product_id),
                            variantId: String(item.shop_external_id),
                            name: item.name || 'Product',
                            sku: item.sku || '',
                            handle: handle,
                            productUrl: item.product_url || ''
                        });
                    }
                });
            }

            console.log('Loaded wishlist from server:', wishlistItems.length, 'items');
            return wishlistItems;
        } catch (error) {
            console.error('Error loading wishlist from server:', error);
            return [];
        }
    }

    /**
     * Upload all local items to server without clearing localStorage
     * Used when localStorage has items but server is empty
     */
    async uploadLocalItemsToServer() {
        console.log('📤 Uploading all local items to server...');

        const localItems = Array.from(this.wishlistItems);
        let successCount = 0;

        for (const key of localItems) {
            const [productId, variantId] = key.split('-');

            try {
                await this.addToServerWishlist(productId, variantId);
                successCount++;
                console.log(`✓ Uploaded ${successCount}/${localItems.length}: variant ${variantId}`);
            } catch (error) {
                console.warn(`Failed to upload variant ${variantId}:`, error);
            }
        }

        console.log(`✅ Upload complete: ${successCount}/${localItems.length} items uploaded to server`);
        console.log(`💾 Local data preserved: ${this.wishlistItems.size} items in localStorage`);
    }

    /**
     * Sync wishlist with server
     * Server is the single source of truth for logged-in users
     * @param {Array} serverItems - Array of items from server with productId, variantId, name, handle, etc.
     * @param {boolean} isInitialSync - True on first login, uploads local items to server
     */
    async syncWithServer(serverItems, isInitialSync = false) {
        console.log(`Syncing with server (Initial sync: ${isInitialSync})...`);

        // Ensure serverItems is an array
        if (!serverItems) {
            serverItems = [];
        }

        // Step 1: If initial sync, upload local-only items to server first
        if (isInitialSync) {
            // Get current local items as variant IDs
            const localVariantIds = new Set();
            Array.from(this.wishlistItems).forEach(key => {
                const [, variantId] = key.split('-');
                localVariantIds.add(variantId);
            });

            // Create map of server variant IDs
            const serverVariantIds = new Set(serverItems.map(item => item.variantId));

            // Find local items that are NOT on server
            const localOnlyItems = [];
            Array.from(this.wishlistItems).forEach(key => {
                const [productId, variantId] = key.split('-');
                if (!serverVariantIds.has(variantId)) {
                    localOnlyItems.push({ productId, variantId });
                }
            });

            console.log(`📤 Uploading ${localOnlyItems.length} local items to server...`);

            // Upload local items to server
            for (const { productId, variantId } of localOnlyItems) {
                try {
                    await this.addToServerWishlist(productId, variantId);
                    console.log(`✓ Uploaded variant ${variantId} to server`);
                } catch (error) {
                    console.warn(`Failed to upload variant ${variantId}:`, error);
                }
            }

            // IMPORTANT: After uploading, reload from server to get fresh data
            if (localOnlyItems.length > 0) {
                console.log(`🔄 Reloading from server after upload...`);
                const freshServerItems = await this.loadFromServer();
                serverItems = freshServerItems;
                console.log(`✓ Reloaded: ${serverItems.length} items from server`);
            }
        }

        // Step 2: Backup existing localStorage data before clearing
        console.log(`📥 Loading ${serverItems.length} items from server as source of truth...`);

        // Backup existing wishlist item data (with images, prices, etc.)
        const existingDataBackup = {};
        for (let i = 0; i < localStorage.length; i++) {
            const storageKey = localStorage.key(i);
            if (storageKey && storageKey.startsWith(`${this.localStorageKey}_item_`)) {
                try {
                    const data = JSON.parse(localStorage.getItem(storageKey));
                    if (data && data.key) {
                        existingDataBackup[data.key] = data;
                    }
                } catch (error) {
                    // Skip invalid data
                }
            }
        }

        console.log(`💾 Backed up ${Object.keys(existingDataBackup).length} items with complete data`);

        // Clear current wishlist
        this.wishlistItems.clear();

        // Clear all individual item data from localStorage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(`${this.localStorageKey}_item_`)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        // Step 3: Add all server items to wishlist, restoring complete data where available
        for (const serverItem of serverItems) {
            const { productId, variantId, name, handle, sku, productUrl } = serverItem;
            const key = `${productId}-${variantId}`;

            // Check if we have backed up complete data for this item
            const existingData = existingDataBackup[key];

            let wishlistItem;

            if (existingData && existingData.product && existingData.variant &&
                (existingData.product.featured_image || existingData.variant.price > 0)) {
                // Restore complete data from backup
                console.log(`✓ ${name} (restored complete data)`);
                wishlistItem = existingData;
            } else {
                // Create new item with API data only
                console.log(`✓ ${name} (basic data from API, SKU: ${sku || 'N/A'})`);
                wishlistItem = {
                    key: key,
                    productId: productId,
                    variantId: variantId,
                    product: {
                        id: productId,
                        title: name || 'Product',
                        handle: handle || '#',
                        vendor: '',
                        featured_image: ''
                    },
                    variant: {
                        id: variantId,
                        title: 'Default Title',
                        price: 0,
                        compare_at_price: null,
                        featured_image: '',
                        sku: sku || ''
                    }
                };
            }

            this.wishlistItems.add(key);
            this.saveWishlistItem(wishlistItem);
        }

        // Save wishlist keys to localStorage
        this.saveToStorage();

        console.log(`✅ Sync complete. Server is now source of truth. ${this.wishlistItems.size} items in wishlist.`);

        // Update all heart icons to reflect synced wishlist state
        this.updateAllHearts();
        this.updateHeaderCount();

        // Dispatch event so wishlist page can reload
        this.triggerWishlistEvent('synced', {
            itemCount: this.wishlistItems.size,
            isInitialSync: isInitialSync
        });
    }

    /**
     * Sync localStorage items to server (called after login)
     */
    async syncLocalStorageToServer() {
        if (!this.isUserLoggedIn) {
            return;
        }

        console.log('Syncing local wishlist to server...');

        const itemsToSync = Array.from(this.wishlistItems);

        for (const key of itemsToSync) {
            const [productId, variantId] = key.split('-');

            try {
                await this.addToServerWishlist(productId, variantId);
            } catch (error) {
                console.warn(`Failed to sync item ${key}:`, error);
                // Continue with other items even if one fails
            }
        }

        console.log('Wishlist sync completed');
    }

    /**
     * Bind click events to wishlist heart buttons
     */
    bindEvents() {
        document.addEventListener('click', (event) => {
            const button = event.target.closest('[data-wishlist-toggle]');
            if (!button) return;

            event.preventDefault();

            const productId = button.getAttribute('data-product-id');
            const variantId = button.getAttribute('data-variant-id');

            if (!productId || !variantId || isNaN(productId) || isNaN(variantId)) {
                return;
            }

            // Helper function to convert relative URLs to absolute
            const makeAbsoluteUrl = (url) => {
                if (!url || url.trim() === '') return '';
                if (url.startsWith('http')) return url; // Already absolute
                if (url.startsWith('//')) {
                    // Protocol-relative URL - just add the protocol
                    return window.location.protocol + url;
                }
                if (url.startsWith('/')) {
                    // Path-only URL - add protocol + hostname
                    const protocol = window.location.protocol;
                    const hostname = window.location.hostname;
                    const port = window.location.port ? ':' + window.location.port : '';
                    return `${protocol}//${hostname}${port}${url}`;
                }
                return url;
            };

            // Extract product and variant data from button attributes
            const rawProductImage = button.getAttribute('data-product-image');
            const rawVariantImage = button.getAttribute('data-variant-image');


            const productData = {
                id: productId,
                title: button.getAttribute('data-product-title'),
                handle: button.getAttribute('data-wishlist-handle') || button.getAttribute('data-product-handle'),
                vendor: button.getAttribute('data-product-vendor'),
                featured_image: makeAbsoluteUrl(rawProductImage)
            };


            // Get price values and handle them properly
            const rawPrice = button.getAttribute('data-variant-price');
            const rawComparePrice = button.getAttribute('data-variant-compare-price');


            const variantData = {
                id: variantId,
                title: button.getAttribute('data-variant-title'),
                price: rawPrice ? parseInt(rawPrice) : 0,
                compare_at_price: rawComparePrice && rawComparePrice !== 'null' && rawComparePrice !== '' ? parseInt(rawComparePrice) : null,
                featured_image: makeAbsoluteUrl(rawVariantImage || rawProductImage),
                sku: button.getAttribute('data-variant-sku') || ''
            };

            // Add loading state
            button.classList.add('wishlist-loading');

            this.toggleWishlistWithData(productId, variantId, productData, variantData)
                .finally(() => {
                    button.classList.remove('wishlist-loading');
                });
        });
    }

    /**
     * Update visual state of all heart buttons
     */
    updateAllHearts() {
        const buttons = document.querySelectorAll('[data-wishlist-toggle]');
        buttons.forEach(button => {
            const key = button.getAttribute('data-wishlist-key');
            if (key) {
                this.updateHeartForItem(key);
            }
        });
    }

    /**
     * Update visual state of heart button for specific item
     */
    updateHeartForItem(wishlistKey) {
        const buttons = document.querySelectorAll(`[data-wishlist-key="${wishlistKey}"]`);
        const isInWishlist = this.wishlistItems.has(wishlistKey);

        buttons.forEach(button => {
            button.classList.toggle('is-in-wishlist', isInWishlist);
            button.setAttribute('aria-pressed', isInWishlist);

            // Update title
            const addText = 'Zur Merkliste hinzufügen';
            const removeText = 'Aus Merkliste entfernen';
            button.setAttribute('title', isInWishlist ? removeText : addText);

            // Trigger animation
            this.animateHeart(button);
        });
    }

    /**
     * Animate heart button on toggle
     */
    animateHeart(button) {
        button.classList.add('wishlist-animate');

        // Remove animation class after animation completes
        setTimeout(() => {
            button.classList.remove('wishlist-animate');
        }, 300);
    }

    /**
     * Trigger custom wishlist events
     */
    triggerWishlistEvent(action, data) {
        const event = new CustomEvent('wishlist:' + action, {
            detail: data,
            bubbles: true
        });
        document.dispatchEvent(event);
    }

    /**
     * Get all wishlist items with complete data
     */
    getWishlistItems() {
        return Array.from(this.wishlistItems).map(key => {
            const [productId, variantId] = key.split('-');
            const storedData = this.getWishlistItem(key);

            if (storedData && storedData.product && storedData.variant) {
                // Return complete stored data
                return storedData;
            } else {
                // Fallback to basic data if no stored product info
                return {productId, variantId, key};
            }
        });
    }

    /**
     * Get wishlist count
     */
    getWishlistCount() {
        return this.wishlistItems.size;
    }


    /**
     * Clear entire wishlist
     */
    clearWishlist() {
        // Remove all individual item data
        Array.from(this.wishlistItems).forEach(key => {
            this.removeWishlistItemData(key);
        });

        this.wishlistItems.clear();
        this.saveToStorage();
        this.updateAllHearts();
        this.updateHeaderCount();
        this.triggerWishlistEvent('cleared', {});
    }

    /**
     * Update the wishlist counter badge in the header
     * Updated to handle multiple counter elements (mobile + desktop)
     */
    updateHeaderCount() {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            // Find ALL counter elements (mobile and desktop)
            const counters = document.querySelectorAll('[data-header-wishlist-count]');

            if (!counters || counters.length === 0) {
                console.warn('Wishlist: No header counter elements found. Retrying...');
                // Retry after a short delay if elements not found
                setTimeout(() => this.updateHeaderCountImmediate(), 100);
                return;
            }

            const count = this.wishlistItems.size;
            console.log(`Wishlist: Updating ${counters.length} counter(s) with count: ${count}`);

            // Update ALL counter elements
            counters.forEach((counter, index) => {
                // Update the counter value attribute
                counter.setAttribute('data-header-wishlist-count', count);

                // Toggle visibility based on count
                if (count > 0) {
                    counter.classList.add('visible');
                } else {
                    counter.classList.remove('visible');
                }

                console.log(`Wishlist: Counter ${index + 1} updated - visible: ${count > 0}`);
            });
        });
    }

    /**
     * Immediate update without requestAnimationFrame (for retry)
     */
    updateHeaderCountImmediate() {
        const counters = document.querySelectorAll('[data-header-wishlist-count]');

        if (!counters || counters.length === 0) {
            console.error('Wishlist: Header counter elements still not found after retry');
            return;
        }

        const count = this.wishlistItems.size;

        counters.forEach((counter) => {
            counter.setAttribute('data-header-wishlist-count', count);

            if (count > 0) {
                counter.classList.add('visible');
            } else {
                counter.classList.remove('visible');
            }
        });
    }

}

// Initialize wishlist when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.productWishlist = new ProductWishlist();

});

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ProductWishlist;
}
